'use client';

import { useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { cn } from '@/helpers/utils';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { useScrollLock } from '@/hooks/use-scroll-lock';
import BurgerButton from './BurgerButton';
import BurgerSubmenu from './BurgerSubmenu';
import Container from './Container';
import LanguageSwitcher from './LanguageSwitcher';
import Logo from './Logo';
import LogoMobile from './LogoMobile';
import NavSubmenu from './NavSubmenu';

type NavLink = {
    href: string;
    label: string;
    mobileOnly?: boolean;
    submenu?: { href: string; label: string }[];
};

export default function Nav() {
    const locale = useLocale();
    const t = useTranslations('common');

    const navRef = useRef<HTMLDivElement>(null);
    const [activeNav, setActiveNav] = useState(false);

    const closeBurger = () => setActiveNav(false);
    useOnClickOutside(navRef, closeBurger);

    // Lock body scroll while the mobile burger menu is open.
    useScrollLock(activeNav);

    const navLinks: NavLink[] = [
        {
            href: '#',
            label: t('pages.programmes'),
            submenu: [
                { href: '/programmes/jobs-in-the-netherlands', label: t('pages.programmeNetherlands') },
                { href: '/programmes/part-time-job-in-amsterdam', label: t('pages.programmeAmsterdam') },
            ],
        },
        { href: '/job-offers', label: t('pages.jobOffers') },
        { href: '/how-it-works', label: t('pages.howItWorks') },
        { href: '/about-us', label: t('pages.aboutUs') },
        { href: '/register', label: t('pages.register'), mobileOnly: true },
        { href: '/contacts', label: t('pages.contacts') },
    ];

    return (
        <nav className="fixed top-0 left-0 w-full z-40">
            <div
                ref={navRef}
                className="bg-white max-w-[1440px] mx-auto rounded-bl-[35px] rounded-br-[35px] shadow-nav"
            >
                <Container as="div">
                    <div>
                        <div className="flex justify-between items-center">
                            <Logo />
                            <LogoMobile active={activeNav} onLanguageChange={closeBurger} />

                            <div
                                className={cn(
                                    'hidden xl:flex items-center',
                                    locale === 'es' ? 'gap-2.5' : 'gap-5'
                                )}
                            >
                                {navLinks.map(({ href, label, submenu, mobileOnly }) => {
                                    if (mobileOnly) return null;

                                    if (submenu) {
                                        return (
                                            <NavSubmenu key={label} items={submenu}>
                                                {label}
                                            </NavSubmenu>
                                        );
                                    }

                                    return (
                                        <Link
                                            href={href}
                                            key={href}
                                            className="inline-flex leading-none mouse-hover:bg-purple-800 mouse-hover:text-white pt-10 pb-6 px-6 mb-4 rounded-bl-[15px] rounded-br-[15px] font-medium transition-colors duration-200"
                                        >
                                            {label}
                                        </Link>
                                    );
                                })}

                                <LanguageSwitcher />
                            </div>

                            <BurgerButton
                                onClick={() => setActiveNav((prev) => !prev)}
                                activeNav={activeNav}
                                label={t('menu')}
                                controls="primary-navigation"
                            />
                        </div>

                        <div id="primary-navigation" className={cn('accordion', activeNav && 'accordion-open')}>
                            <div className="pb-7 text-center flex flex-col items-center">
                                {navLinks.map(({ href, label, submenu }) => {
                                    if (submenu) {
                                        return (
                                            <BurgerSubmenu
                                                key={label}
                                                items={submenu}
                                                activeNav={activeNav}
                                                onClick={closeBurger}
                                            >
                                                {label}
                                            </BurgerSubmenu>
                                        );
                                    }

                                    return (
                                        <Link
                                            href={href}
                                            key={href}
                                            className="inline-flex justify-center leading-none p-6 font-medium"
                                            onClick={closeBurger}
                                        >
                                            {label}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </Container>
            </div>
        </nav>
    );
}
